/**
 * Formats a time difference into a user-friendly string
 * @param date - The target date
 * @param referenceDate - The reference date (defaults to current time)
 * @returns A string like "1 minute ago", "in 4 days", "just now", etc.
 */
export const formatTimeAgo = (date?: Date, referenceDate: Date = new Date()): string => {
  if (!date) {
    return 'N/A';
  }
  const diffMs = date.getTime() - referenceDate.getTime();
  const isPast = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // Handle very small differences
  if (seconds < 30) {
    return 'just now';
  }

  let timeUnit: string;
  let timeValue: number;

  if (years > 0) {
    timeValue = years;
    timeUnit = years === 1 ? 'year' : 'years';
  } else if (months > 0) {
    timeValue = months;
    timeUnit = months === 1 ? 'month' : 'months';
  } else if (weeks > 0) {
    timeValue = weeks;
    timeUnit = weeks === 1 ? 'week' : 'weeks';
  } else if (days > 0) {
    timeValue = days;
    timeUnit = days === 1 ? 'day' : 'days';
  } else if (hours > 0) {
    timeValue = hours;
    timeUnit = hours === 1 ? 'hour' : 'hours';
  } else {
    timeValue = minutes;
    timeUnit = minutes === 1 ? 'minute' : 'minutes';
  }

  if (isPast) {
    return `${timeValue} ${timeUnit} ago`;
  } else {
    return `in ${timeValue} ${timeUnit}`;
  }
};

/**
 * Formats a countdown to a future date
 * @param targetDate - The target future date
 * @param referenceDate - The reference date (defaults to current time)
 * @returns A string like "4 days", "2 hours", or null if the date has passed
 */
export const formatCountdown = (targetDate: Date, referenceDate: Date = new Date()): string | null => {
  const diffMs = targetDate.getTime() - referenceDate.getTime();

  // If the date has passed, return null
  if (diffMs <= 0) {
    return null;
  }

  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (weeks > 0) {
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  } else if (days > 0) {
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  } else if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  } else if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  } else {
    return 'less than a minute';
  }
};

/**
 * Formats a time difference with more granular control
 * @param date - The target date
 * @param referenceDate - The reference date (defaults to current time)
 * @param options - Formatting options
 * @returns A formatted string
 */
export const formatTimeDelta = (
  date: Date,
  referenceDate: Date = new Date(),
  options: {
    showDirection?: boolean; // Whether to show "ago" or "in"
    maxUnit?: 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';
    shortForm?: boolean; // Use short forms like "1m" instead of "1 minute"
  } = {}
): string => {
  const { showDirection = true, maxUnit = 'year', shortForm = false } = options;

  const diffMs = date.getTime() - referenceDate.getTime();
  const isPast = diffMs < 0;
  const absDiffMs = Math.abs(diffMs);

  const seconds = Math.floor(absDiffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 30 && !shortForm) {
    return 'just now';
  }

  let timeValue: number;
  let timeUnit: string;

  // Determine the appropriate unit based on maxUnit constraint
  if (maxUnit === 'minute' || minutes === 0) {
    timeValue = Math.max(1, minutes);
    timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';
  } else if (maxUnit === 'hour' || hours === 0) {
    timeValue = Math.max(1, minutes);
    timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';

    if (hours > 0) {
      timeValue = hours;
      timeUnit = shortForm ? 'h' : timeValue === 1 ? 'hour' : 'hours';
    }
  } else if (maxUnit === 'day' || days === 0) {
    if (hours > 0) {
      timeValue = hours;
      timeUnit = shortForm ? 'h' : timeValue === 1 ? 'hour' : 'hours';
    } else {
      timeValue = Math.max(1, minutes);
      timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';
    }

    if (days > 0) {
      timeValue = days;
      timeUnit = shortForm ? 'd' : timeValue === 1 ? 'day' : 'days';
    }
  } else if (maxUnit === 'week' || weeks === 0) {
    if (days > 0) {
      timeValue = days;
      timeUnit = shortForm ? 'd' : timeValue === 1 ? 'day' : 'days';
    } else if (hours > 0) {
      timeValue = hours;
      timeUnit = shortForm ? 'h' : timeValue === 1 ? 'hour' : 'hours';
    } else {
      timeValue = Math.max(1, minutes);
      timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';
    }

    if (weeks > 0) {
      timeValue = weeks;
      timeUnit = shortForm ? 'w' : timeValue === 1 ? 'week' : 'weeks';
    }
  } else if (maxUnit === 'month' || months === 0) {
    if (weeks > 0) {
      timeValue = weeks;
      timeUnit = shortForm ? 'w' : timeValue === 1 ? 'week' : 'weeks';
    } else if (days > 0) {
      timeValue = days;
      timeUnit = shortForm ? 'd' : timeValue === 1 ? 'day' : 'days';
    } else if (hours > 0) {
      timeValue = hours;
      timeUnit = shortForm ? 'h' : timeValue === 1 ? 'hour' : 'hours';
    } else {
      timeValue = Math.max(1, minutes);
      timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';
    }

    if (months > 0) {
      timeValue = months;
      timeUnit = shortForm ? 'mo' : timeValue === 1 ? 'month' : 'months';
    }
  } else {
    // maxUnit === 'year' or no constraint
    if (years > 0) {
      timeValue = years;
      timeUnit = shortForm ? 'y' : timeValue === 1 ? 'year' : 'years';
    } else if (months > 0) {
      timeValue = months;
      timeUnit = shortForm ? 'mo' : timeValue === 1 ? 'month' : 'months';
    } else if (weeks > 0) {
      timeValue = weeks;
      timeUnit = shortForm ? 'w' : timeValue === 1 ? 'week' : 'weeks';
    } else if (days > 0) {
      timeValue = days;
      timeUnit = shortForm ? 'd' : timeValue === 1 ? 'day' : 'days';
    } else if (hours > 0) {
      timeValue = hours;
      timeUnit = shortForm ? 'h' : timeValue === 1 ? 'hour' : 'hours';
    } else {
      timeValue = Math.max(1, minutes);
      timeUnit = shortForm ? 'm' : timeValue === 1 ? 'minute' : 'minutes';
    }
  }

  const timeString = shortForm ? `${timeValue}${timeUnit}` : `${timeValue} ${timeUnit}`;

  if (!showDirection) {
    return timeString;
  }

  if (isPast) {
    return `${timeString} ago`;
  } else {
    return `in ${timeString}`;
  }
};

export const daysToMs = (days: number) => days * 24 * 60 * 60 * 1000;
export const hoursToMs = (hours: number) => hours * 60 * 60 * 1000;
export const minutesToMs = (minutes: number) => minutes * 60 * 1000;
export const secondsToMs = (seconds: number) => seconds * 1000;

/** The date and time in the browser's locale, or 'N/A' when there is no date. */
export const formatDateTime = (date?: Date): string => (date ? date.toLocaleString() : 'N/A');
